// ─────────────────────────────────────────────────────────────
//  Academy CMS data layer (Realtime Database)
//
//  The whole Academy content tree lives under `salesgod-research/academy`.
//  Public read is allowed (so the Academy frontend can later load from here);
//  writes are admin-only and enforced by `database.rules.json`.
//
//  Hierarchy:
//    academy/
//      tiers   : [ { id, name, requiredTracks, blurb } ]        (array)
//      badges  : { badgeId: { name, color, description } }       (catalog map)
//      topics  : { topicId: { ...topic, courses: { courseId: { ...course,
//                  lessons: { lessonId: { ...lesson } } } } } }
//
//  Sub-lists (valueVault, cheatsheets, quiz.questions, practiceLab.blocks,
//  angles, structure, variables, promptExamples) are stored as plain arrays
//  to keep the editor simple. Topics / courses / lessons use keyed maps so the
//  keys can serve as the URL slugs the public routes already rely on.
// ─────────────────────────────────────────────────────────────
import { ref, get, set, update, remove, onValue } from 'firebase/database';
import {
  ref as storageRef,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage';
import { database, storage, auth } from '../firebaseConfig';
import { topics as staticTopics, tiers as staticTiers, badgeDesigns } from '../Academy/data/academyData';

export const ACADEMY_ROOT = 'salesgod-research/academy';

const path = (...parts) => [ACADEMY_ROOT, ...parts.filter(Boolean)].join('/');

// ── Slug / id helpers ──
export const slugify = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[äàá]/g, 'a')
    .replace(/[öòó]/g, 'o')
    .replace(/[üùú]/g, 'u')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

// Make `base` unique against a set of existing ids by appending -2, -3, …
export const uniqueId = (base, existingIds = []) => {
  const root = slugify(base) || `item-${Date.now().toString(36)}`;
  if (!existingIds.includes(root)) return root;
  let n = 2;
  while (existingIds.includes(`${root}-${n}`)) n += 1;
  return `${root}-${n}`;
};

// ── Normalization (map → sorted array, keeping ids) ──
const sortByOrder = (a, b) =>
  (a.order ?? 0) - (b.order ?? 0) || String(a.id).localeCompare(String(b.id));

const mapToArray = (mapObj) =>
  mapObj && typeof mapObj === 'object'
    ? Object.entries(mapObj).map(([id, value]) => ({ id, ...value }))
    : [];

export const normalizeAcademy = (raw) => {
  const data = raw || {};
  const topics = mapToArray(data.topics)
    .map((topic) => {
      const courses = mapToArray(topic.courses)
        .map((course) => {
          const lessons = mapToArray(course.lessons).sort(sortByOrder);
          return { ...course, lessons };
        })
        .sort(sortByOrder);
      return { ...topic, courses };
    })
    .sort(sortByOrder);

  return {
    tiers: Array.isArray(data.tiers) ? data.tiers : [],
    badges: data.badges || {},
    topics,
    isEmpty: topics.length === 0,
  };
};

// ── Live subscription ──
// cb receives the normalized academy object. Returns an unsubscribe fn.
export const subscribeAcademy = (cb) => {
  const node = ref(database, ACADEMY_ROOT);
  return onValue(node, (snap) => cb(normalizeAcademy(snap.val())));
};

export const fetchAcademyOnce = async () => {
  const snap = await get(ref(database, ACADEMY_ROOT));
  return normalizeAcademy(snap.val());
};

// ── Generic writers ──
// RTDB rejects writes that contain `undefined` or `NaN`. Strip those (keeping
// `null`, which intentionally deletes a node) so a save can never hard-crash.
const sanitize = (value) => {
  if (value === undefined) return null;
  if (typeof value === 'number' && Number.isNaN(value)) return null;
  if (Array.isArray(value)) return value.map((v) => sanitize(v));
  if (value && typeof value === 'object') {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      if (v === undefined) return; // drop the key entirely
      out[k] = sanitize(v);
    });
    return out;
  }
  return value;
};

const stamp = (data) => sanitize({ ...data, updatedAt: Date.now() });

export const writeTopic = (topicId, data) =>
  update(ref(database, path('topics', topicId)), stamp(data));

export const writeCourse = (topicId, courseId, data) =>
  update(ref(database, path('topics', topicId, 'courses', courseId)), stamp(data));

export const writeLesson = (topicId, courseId, lessonId, data) =>
  update(
    ref(database, path('topics', topicId, 'courses', courseId, 'lessons', lessonId)),
    stamp(data)
  );

// Reorder lessons within a course. `orderedLessonIds` is the lesson ids in the
// desired order; their `order` fields are rewritten to 0..n-1 in a SINGLE
// atomic multi-path update (one DB write, no per-lesson round-trips). This also
// repairs any sparse/duplicate `order` values along the way.
export const reorderLessons = (topicId, courseId, orderedLessonIds = []) => {
  const updates = {};
  orderedLessonIds.forEach((id, index) => {
    updates[`${id}/order`] = index;
  });
  return update(ref(database, path('topics', topicId, 'courses', courseId, 'lessons')), updates);
};

// Replace a whole node (used for nested objects like quiz / practiceLab).
export const setNode = (subPath, value) => set(ref(database, path(subPath)), sanitize(value));

export const removeTopic = (topicId) => remove(ref(database, path('topics', topicId)));
export const removeCourse = (topicId, courseId) =>
  remove(ref(database, path('topics', topicId, 'courses', courseId)));
export const removeLesson = (topicId, courseId, lessonId) =>
  remove(ref(database, path('topics', topicId, 'courses', courseId, 'lessons', lessonId)));

export const writeTiers = (tiers) => set(ref(database, path('tiers')), tiers);
export const writeBadges = (badges) => set(ref(database, path('badges')), badges);

// ── Cover / image upload (Firebase Storage, admin only) ──
// scope is a short folder hint, e.g. 'topics' or 'courses'.
export const uploadImage = async (file, scope = 'misc') => {
  if (!file) throw new Error('No file');
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const fullPath = `salesgod-research/academy/${scope}/${Date.now()}_${safeName}`;
  const sRef = storageRef(storage, fullPath);
  await uploadBytes(sRef, file);
  const url = await getDownloadURL(sRef);
  return { url, path: fullPath };
};

// Map a file to one of the icon "types" the Academy frontend understands.
export const detectFileType = (file) => {
  const mime = (file?.type || '').toLowerCase();
  const ext = (file?.name?.split('.').pop() || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (
    mime.includes('word') ||
    mime.includes('officedocument') ||
    mime.includes('ms-') ||
    ['doc', 'docx', 'rtf', 'odt', 'txt', 'md', 'xls', 'xlsx', 'ppt', 'pptx', 'csv'].includes(ext)
  ) {
    return 'doc';
  }
  return 'doc';
};

// ── Generic file upload (images, PDFs, docs …) for resources & cheatsheets ──
// Returns { url, path, name, type, size }. `type` feeds the frontend icon.
export const uploadFile = async (file, scope = 'files') => {
  if (!file) throw new Error('Keine Datei');
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const fullPath = `salesgod-research/academy/${scope}/${Date.now()}_${safeName}`;
  const sRef = storageRef(storage, fullPath);
  await uploadBytes(sRef, file, { contentType: file.type || undefined });
  const url = await getDownloadURL(sRef);
  return { url, path: fullPath, name: file.name, type: detectFileType(file), size: file.size };
};

// ── Video upload (Firebase Storage, admin only) ──
// Videos werden dauerhaft in Storage abgelegt und direkt über die
// Download-URL ausgeliefert. Für HD-Material mit geringen Abrufzahlen reicht
// das (Storage unterstützt Byte-Range-Requests → Streaming/Seeking im
// <video>-Element), ein CDN/Transcoding ist dafür nicht nötig.
const ACADEMY_VIDEO_DIR = 'salesgod-research/academy-videos';

// Upload a video file directly to permanent Storage.
// `onProgress(percent)` is called with 0–100 while uploading.
// Resolves with { videoUrl, path }.
export const uploadVideo = (file, { onProgress } = {}) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('Keine Datei ausgewählt.'));
      return;
    }
    if (!file.type || !file.type.startsWith('video/')) {
      reject(new Error('Bitte eine Videodatei auswählen.'));
      return;
    }
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const fullPath = `${ACADEMY_VIDEO_DIR}/${Date.now()}_${safeName}`;
    const task = uploadBytesResumable(storageRef(storage, fullPath), file, {
      contentType: file.type,
    });
    task.on(
      'state_changed',
      (snap) => {
        if (onProgress && snap.totalBytes) {
          onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        }
      },
      (err) => reject(err),
      async () => {
        try {
          const videoUrl = await getDownloadURL(task.snapshot.ref);
          resolve({ videoUrl, path: fullPath });
        } catch (err) {
          reject(err);
        }
      }
    );
  });

// ── AI: generate a Practice-Lab draft (admin only) ──
const GENERATE_LAB_URL =
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateAcademyLab';

// Ask the Cloud Function (Claude) for a ready-to-edit lab for the given mode.
// `difficulty` ∈ 'anfaenger' | 'mittel' | 'profi'. Returns the lab object.
export const generateLab = async ({ mode, difficulty, topic, instructions } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Nicht angemeldet.');
  const token = await user.getIdToken();
  const res = await fetch(GENERATE_LAB_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ mode, difficulty, topic, instructions }),
  });
  let payload = {};
  try {
    payload = await res.json();
  } catch (e) {
    payload = {};
  }
  if (!res.ok || !payload.ok) {
    throw new Error(payload.error || `Generierung fehlgeschlagen (HTTP ${res.status}).`);
  }
  return { lab: payload.lab, lesson: payload.lesson || null };
};


// ── Seed: transfer the current hardcoded demo content into RTDB ──
// Converts the static academyData arrays into the keyed-map structure above.
export const buildSeedTree = () => {
  const badges = {};
  Object.entries(badgeDesigns || {}).forEach(([id, design]) => {
    badges[id] = { name: id, color: design.color || '#6da7ed', description: design.description || '' };
  });

  const topics = {};
  (staticTopics || []).forEach((topic, ti) => {
    const courses = {};
    (topic.courses || []).forEach((course, ci) => {
      const lessons = {};
      let order = 0;
      // Each legacy lesson may bundle video + quiz + practice. The new model is
      // one lesson = one type, so split bundled lessons into separate steps:
      // video (+ cheatsheets) → practice → quiz.
      (course.lessons || []).forEach((lesson) => {
        lessons[lesson.id] = {
          type: 'video',
          title: lesson.title || '',
          label: lesson.label || '',
          duration: lesson.duration || '',
          durationSeconds: lesson.durationSeconds || 0,
          description: lesson.description || '',
          video: { provider: 'bunny', bunnyVideoId: '', url: '' },
          preview: lesson.preview || '',
          cheatsheets: lesson.cheatsheets || [],
          order: order++,
        };
        if (lesson.practiceLab) {
          lessons[`${lesson.id}-practice`] = {
            type: 'practice',
            title: lesson.practiceLab.title || `${lesson.title || 'Lektion'} – Practice Lab`,
            label: lesson.label || '',
            description: lesson.practiceLab.brief || '',
            practiceLab: lesson.practiceLab,
            order: order++,
          };
        }
        if (lesson.quiz) {
          lessons[`${lesson.id}-quiz`] = {
            type: 'quiz',
            title: `${lesson.title || 'Lektion'} – Mini-Quiz`,
            label: lesson.label || '',
            description: '',
            quiz: lesson.quiz,
            order: order++,
          };
        }
      });
      // course.badge may exist; keep its id+name reference
      if (course.badge && course.badge.id && badges[course.badge.id]) {
        badges[course.badge.id].name = course.badge.name || badges[course.badge.id].name;
      }
      courses[course.id] = {
        title: course.title || '',
        subtitle: course.subtitle || '',
        description: course.description || '',
        badge: course.badge || null,
        lessons,
        order: ci,
      };
    });
    topics[topic.id] = {
      title: topic.title || '',
      tagline: topic.tagline || '',
      description: topic.description || '',
      accent: topic.accent || '#6da7ed',
      image: topic.image || '',
      valueVault: topic.valueVault || [],
      courses,
      order: ti,
    };
  });

  return {
    tiers: (staticTiers || []).map((t) => ({ ...t })),
    badges,
    topics,
    meta: { seededAt: Date.now() },
  };
};

// Writes the seed tree. By default refuses to overwrite an existing tree.
export const seedAcademy = async ({ overwrite = false } = {}) => {
  const existing = await get(ref(database, path('topics')));
  if (existing.exists() && !overwrite) {
    throw new Error('Academy data already exists. Pass overwrite to replace it.');
  }
  await set(ref(database, ACADEMY_ROOT), buildSeedTree());
};
